<template>
  <hs-modal :model-value="true" title="Edit subscription message" size="lg" @close="close">
    <div class="space-y-4">
      <p class="text-sm">{{ template.key }} · {{ template.channel }} · Version {{ template.version }}</p>
      <p class="text-sm text-gray-500">Publishing creates a new active version. Existing delivery history keeps its original content.</p>
      <hs-select v-model="language" label="Language to edit" :options="languageOptions" :disabled="saving || previewing" />
      <hs-select v-model="draft.default_language" label="Fallback language" :options="languageOptions" :disabled="saving || previewing" />
      <template v-if="template.channel === 'EMAIL'">
        <hs-input v-model="variant.subject" label="Subject" :disabled="saving || previewing" />
        <hs-input v-model="variant.headline" label="Headline" :disabled="saving || previewing" />
      </template>
      <label class="block text-sm font-medium">{{ template.channel === 'EMAIL' ? 'Email body (HTML)' : 'SMS body' }}
        <textarea v-model="variant.body" rows="10" class="message-editor" :disabled="saving || previewing" />
      </label>
      <p class="text-xs text-gray-500">Available variables: {{ availableVariables }}</p>
      <label class="block text-sm font-medium">Sample values for preview (JSON)
        <textarea v-model="sampleText" rows="5" class="message-editor" :disabled="saving || previewing" />
      </label>
      <hs-button variant="secondary" :loading="previewing" :disabled="saving || previewing" @click="previewMessage">Preview</hs-button>
      <hs-alert v-if="error" tone="danger" :message="error" />
      <section v-if="preview" class="space-y-2 rounded-lg border p-3">
        <p v-if="preview.subject" class="font-semibold text-sm">{{ preview.subject }}</p>
        <iframe v-if="template.channel === 'EMAIL'" title="Email preview" sandbox="" :srcdoc="preview.content" class="message-preview" />
        <pre v-else class="whitespace-pre-wrap text-sm">{{ preview.content }}</pre>
      </section>
    </div>
    <template #footer>
      <hs-button variant="secondary" :disabled="saving || previewing" @click="close">Cancel</hs-button>
      <hs-button :loading="saving" :disabled="previewing" @click="save">Publish new version</hs-button>
    </template>
  </hs-modal>
</template>

<script>
import { HsModal, HsInput, HsSelect, HsButton, HsAlert } from "@/components/ui";
import http from "@/helper/http";

export default {
  name: "SubscriptionMessageTemplateModal",
  components: { HsModal, HsInput, HsSelect, HsButton, HsAlert },
  props: { template: { type: Object, required: true } },
  emits: ["close", "saved"],
  data() {
    return {
      draft: { default_language: this.template.default_language, localized_variants: JSON.parse(JSON.stringify(this.template.localized_variants)) },
      language: this.template.default_language,
      sampleText: JSON.stringify(this.template.sample_values || {}, null, 2),
      saving: false, previewing: false, error: "", preview: null,
    };
  },
  computed: {
    availableVariables() { return (this.template.variable_schema || []).map((name) => `{${name}}`).join(", "); },
    languageOptions() { return Object.keys(this.draft.localized_variants).map((value) => ({ value, label: { EN: "English", RW: "Kinyarwanda" }[value] || value })); },
    variant() { return this.draft.localized_variants[this.language]; },
  },
  watch: {
    draft: { deep: true, handler() { this.preview = null; } },
    language() { this.preview = null; },
    sampleText() { this.preview = null; },
  },
  methods: {
    close() { if (!this.saving) this.$emit("close"); },
    payload() {
      let samples;
      try { samples = JSON.parse(this.sampleText); } catch { throw new Error("Sample values must be valid JSON."); }
      if (!samples || Array.isArray(samples) || typeof samples !== "object") throw new Error("Sample values must be a JSON object.");
      return { ...this.draft, variable_schema: this.template.variable_schema, sample_values: samples };
    },
    async previewMessage() {
      if (this.previewing) return;
      this.error = "";
      this.previewing = true;
      try {
        const draft = this.payload();
        const { data } = await http.post(`/v1/admin/messaging/templates/${this.template.id}/preview`, { language: this.language, values: draft.sample_values, draft });
        this.preview = data;
      } catch (error) { this.error = error?.response?.data?.error?.message || error.message || "Unable to preview message."; }
      finally { this.previewing = false; }
    },
    async save() {
      if (this.saving || this.previewing) return;
      this.saving = true;
      this.error = "";
      try {
        const { data } = await http.patch(`/v1/admin/messaging/templates/${this.template.id}`, this.payload());
        this.$emit("saved", data);
      } catch (error) { this.error = error?.response?.data?.error?.message || error.message || "Unable to publish message."; }
      finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
.message-editor { display: block; width: 100%; margin-top: 0.5rem; padding: 0.75rem; border: 1px solid var(--hs-border, #e2e8f0); border-radius: 0.5rem; background: var(--hs-surface, #fff); color: var(--hs-text, #0f172a); font: inherit; }
.message-preview { width: 100%; min-height: 360px; border: 0; background: white; }
</style>
